import { useCallback, useMemo } from 'react';
import { useDisclosure } from '@shared/hooks';
import { useToast } from '@retail/hooks';
import { useAppTFunction } from '@retail/app/i18n';
import { generateXLSX, XLSXRow } from '@retail/products/utils';

import { fetchPaginatedDataInParallel } from '@shared/fetch-utils';
import dayjs from 'dayjs';
import { SearchOptions, useMutateMgSupplierItemsSearch } from '../mgSupplierItemSearchApi';
import {
  supplierItemsColumnHeaders,
  SupplierItemsExportColumn,
} from './supplierItemsColumnHeaders';
import { MgSupplierItemSearchResult } from '@retail/products/types';
import { supplierItemsValueMap } from './supplierItemsValueMap';

interface Props {
  filters: Omit<SearchOptions, 'page' | 'size'>;
}

export const useExportSupplierItemsToExcel = ({ filters }: Props) => {
  const t = useAppTFunction();
  const toast = useToast();
  const { isOpen: isLoading, onOpen: onLoadingStart, onClose: onLoadingEnd } = useDisclosure(false);
  const { mutateAsync: loadDataForExport } = useMutateMgSupplierItemsSearch();

  const exportToExcel = useCallback(async () => {
    onLoadingStart();

    const columns = Object.keys(supplierItemsColumnHeaders(t)) as SupplierItemsExportColumn[];

    try {
      const pageSizeChunk = 10000;

      const allItems = await fetchPaginatedDataInParallel({
        fetchFn: ({ page }) =>
          loadDataForExport({
            urlVariables: { ...filters, page, size: pageSizeChunk },
          }),
        maxParallellRequestsCount: 3,
      });

      const headerRow: XLSXRow = columns.map((column) => supplierItemsColumnHeaders(t)[column]);

      const dataRows: XLSXRow[] = MgSupplierItemSearchResult.fromSearchResultDTOs(allItems, []).map(
        (item) => columns.map((column) => supplierItemsValueMap[column](item, t))
      );

      await generateXLSX({
        rows: [headerRow, ...dataRows],
        fileName: `supplierItemsSelection ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`,
      });

      toast.success(t('export.messages.fileDownloaded'));
    } catch (error: any) {
      console.error(error);
      toast.error(t('common.genericError'));
    }

    onLoadingEnd();
  }, [filters, loadDataForExport, onLoadingEnd, onLoadingStart, t, toast]);

  return useMemo(
    () => ({
      isLoading,
      exportToExcel,
    }),
    [exportToExcel, isLoading]
  );
};
